<template>
  <div class="header-component-root">
    <!-- Logout Loader Overlay -->
    <div
      v-if="showLogoutLoader"
      class="fixed inset-0 bg-white z-50 flex items-center justify-center"
    >
      <LottieLoader
        type="logout"
        :user-type="userType"
      />
    </div>

    <header
      class="header flex shrink-0 items-center gap-3 border-b border-gray-200/80 bg-white/95 py-4 px-6 backdrop-blur-sm supports-[backdrop-filter]:bg-white/80 lg:px-8"
    >
      <!-- Mobile menu: opens drawer sidebar (branding lives in sidenav) -->
      <button
        type="button"
        class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/30 lg:hidden"
        :aria-expanded="sidebarOpen"
        aria-controls="app-sidenav"
        aria-label="Open menu"
        @click="$emit('toggleSidebar')"
      >
        <i
          class="fas fa-bars text-xl"
          aria-hidden="true"
        />
      </button>
      <div
        class="min-w-0 flex-1"
        aria-hidden="true"
      />

      <!-- right: assistance, notifications, profile (stack below on mobile) -->
      <div
        class="right-profile flex shrink-0 items-center justify-end space-x-4 sm:space-x-6 sm:mt-0"
      >
        <!-- Need Assistance (hide when < 1024px for cleaner mobile/tablet header) -->
        <!-- <button
        class="hidden lg:flex items-center space-x-1 text-blue-500 hover:text-blue-600"
      >
        <span class="text-sm md:text-xs">Need Assistance?</span>
        <i class="fas fa-chevron-down text-sm md:text-xs"></i>
      </button> -->

        <div
          v-if="canViewNotifications"
          class="relative"
        >
          <button
            type="button"
            class="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
            aria-label="Open notifications"
            @click.stop="toggleNotifications"
          >
            <i
              class="fas fa-bell text-base"
              aria-hidden="true"
            />
            <span
              v-if="unreadCount"
              class="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#d92d20] px-1.5 text-[0.65rem] font-bold text-white"
            >
              {{ unreadCount > 9 ? "9+" : unreadCount }}
            </span>
          </button>

          <transition name="fade">
            <div
              v-if="showNotifications"
              ref="notificationPanel"
              class="absolute right-0 z-50 mt-3 w-[22rem] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
            >
              <div
                class="flex items-center justify-between border-b border-gray-100 px-4 py-3"
              >
                <div>
                  <p class="text-sm font-semibold text-gray-900">
                    Renewal Notifications
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ unreadCount }} unread
                  </p>
                </div>
                <button
                  v-if="unreadNotifications.length"
                  type="button"
                  :disabled="notificationsSaving || notificationsLoading || !!notificationsError"
                  class="text-xs font-semibold text-blue-600 transition hover:text-blue-700"
                  @click="markAllAsReadAndClose"
                >
                  Mark all read
                </button>
              </div>

              <div
                v-if="notificationsLoading"
                class="px-4 py-6 text-sm text-gray-500"
              >
                Loading notifications...
              </div>
              <div
                v-else-if="notificationsError"
                class="px-4 py-6 text-sm text-red-500"
              >
                Unable to load notifications right now.
              </div>
              <div
                v-else-if="!unreadNotifications.length"
                class="px-4 py-6 text-sm text-gray-500"
              >
                No renewal alerts right now.
              </div>
              <div
                v-else
                class="max-h-[24rem] overflow-y-auto"
              >
                <button
                  v-for="notification in unreadNotifications.slice(0, 8)"
                  :key="notification.id"
                  type="button"
                  class="flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left transition hover:bg-gray-50"
                  @click="openNotification(notification)"
                >
                  <span
                    class="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                    :class="notificationToneClass(notification.tone)"
                  />
                  <div class="min-w-0 flex-1">
                    <div class="flex items-start justify-between gap-3">
                      <p class="text-sm font-semibold text-gray-900">
                        {{ notification.title }}
                      </p>
                    </div>
                    <p class="mt-1 text-sm leading-5 text-gray-600">
                      {{ notification.message }}
                    </p>
                  </div>
                </button>
              </div>
              <router-link
                to="/settings/notifications"
                class="block border-t border-gray-100 px-4 py-3 text-center text-sm font-semibold text-blue-600 hover:bg-blue-50"
                @click="showNotifications = false"
              >
                View all notifications
              </router-link>
            </div>
          </transition>
        </div>

        <!-- Profile dropdown -->
        <div class="relative">
          <div
            class="flex items-center space-x-2 cursor-pointer"
            @click="toggleDropdown"
          >
            <div
              class="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-gray-700 font-semibold text-sm sm:text-base shrink-0"
              aria-hidden="true"
            >
              {{ profileInitials }}
            </div>
            <span class="hidden sm:inline font-medium text-gray-800">
              {{ headerDisplayName }}
            </span>
            <i class="hidden sm:inline fas fa-chevron-down text-gray-500" />
          </div>

          <!-- dropdown menu -->
          <transition name="fade">
            <div
              v-if="showDropdown"
              ref="dropdown"
              class="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-6rem)] overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-2xl z-50"
            >
              <!-- Profile Header Section -->
              <div :class="userType === 'client' ? 'border-b border-gray-100' : 'p-6 border-b border-gray-100'">
                <div
                  v-if="userType !== 'client'"
                  class="flex items-center justify-end mb-4"
                >
                  <!-- <h3 class="text-lg font-semibold text-gray-800">Profile</h3> -->
                  <button
                    class="text-gray-400 hover:text-gray-600 transition-colors"
                    @click="toggleDropdown"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <!-- User Info -->
                <div
                  v-if="userType !== 'client'"
                  class="text-center mb-4"
                >
                  <div class="relative inline-block">
                    <div
                      class="w-16 h-16 rounded-full border-4 border-green-200 mx-auto mb-3 bg-gray-100 flex items-center justify-center text-xl font-semibold text-gray-700"
                      aria-hidden="true"
                    >
                      {{ profileInitials }}
                    </div>
                  </div>
                  <h4 class="text-lg font-semibold text-gray-800 mb-1">
                    {{ companyName }}
                  </h4>
                  <!-- <p class="text-sm text-gray-500">
                    {{ displayEmail || "" }}
                  </p> -->
                  <p class="text-sm text-gray-500">
                    {{ profileRoleLabel }}
                  </p>
                </div>

                <!-- Account Switcher -->
                <ClientProfileSwitcher
                  v-if="userType === 'client'"
                  :company-name="companyName || ''"
                />
                <!-- <div class="flex items-center justify-center space-x-2 mb-4">
                <div
                  class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium"
                >
                  {{ companyName?.charAt(0) || "A" }}
                </div>
                <div
                  class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium"
                >
                  T
                </div>
                <div
                  class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-medium"
                >
                  M
                </div>
                <button
                  class="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    ></path>
                  </svg>
                </button>
              </div> -->
              </div>

              <!-- Navigation Menu -->
              <!-- <div class="p-4">
              <nav class="space-y-1">
                <button
                  @click="navigateTo('/overview')"
                  class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <svg
                    class="w-5 h-5 mr-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"
                    ></path>
                  </svg>
                  Dashboard
                </button>

                <button
                  @click="navigateTo('/profile')"
                  class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <svg
                    class="w-5 h-5 mr-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                  </svg>
                  Profile
                </button>

                <button
                  @click="navigateTo('/projects')"
                  class="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div class="flex items-center">
                    <svg
                      class="w-5 h-5 mr-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      ></path>
                    </svg>
                    Projects
                  </div>
                  <span
                    class="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full"
                    >3</span
                  >
                </button>

                <button
                  @click="navigateTo('/subscription')"
                  class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <svg
                    class="w-5 h-5 mr-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    ></path>
                  </svg>
                  Subscription
                </button>

                <button
                  @click="navigateTo('/security')"
                  class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <svg
                    class="w-5 h-5 mr-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    ></path>
                  </svg>
                  Security
                </button>

                <button
                  @click="navigateTo('/account-settings')"
                  class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <svg
                    class="w-5 h-5 mr-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                  Account Settings
                </button>
              </nav>
            </div> -->

              <!-- Promotional Banner -->
              <!-- <div
              class="p-4 bg-gradient-to-r from-orange-400 via-pink-500 to-purple-600"
            >
              <div class="flex items-center justify-between">
                <div class="text-white">
                  <div class="text-lg font-bold mb-1">35% OFF</div>
                  <div class="text-sm opacity-90">Power up Productivity!</div>
                </div>
                <div class="flex items-center space-x-2">
                  <button
                    class="bg-yellow-400 text-gray-800 px-3 py-1 rounded-lg text-sm font-medium hover:bg-yellow-300 transition-colors"
                  >
                    Upgrade to Pro
                  </button>
                  <div class="w-8 h-8">
                    <svg
                      class="w-full h-full text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div> -->

              <div class="px-4 pb-4">
                <button
                  class="w-full flex items-center justify-center px-4 py-3 bg-gray-50 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors"
                  @click="openProfilePage"
                >
                  <svg
                    class="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Profile
                </button>
              </div>

              <!-- Logout Section -->
              <div class="p-4 border-t border-gray-100">
                <button
                  class="w-full flex items-center justify-center px-4 py-3 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors"
                  @click="openLogoutModal"
                >
                  <svg
                    class="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </header>

    <!-- Logout modal: outside sticky header (z-index raised so it sits above shell); same copy and classes as before -->
    <div
      v-if="showLogoutModal"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
    >
      <div
        class="w-11/12 max-w-md rounded-lg bg-white p-6 text-center shadow-lg"
      >
        <h2 class="mb-4 text-xl font-semibold">
          Confirm Logout
        </h2>
        <p class="mb-6 text-gray-600">
          Are you sure you want to log out?
        </p>
        <div class="flex justify-center space-x-4">
          <button
            class="rounded-md bg-gray-300 px-4 py-2 transition hover:bg-gray-400"
            @click="showLogoutModal = false"
          >
            Cancel
          </button>
          <button
            class="rounded-md bg-red-500 px-4 py-2 text-white transition hover:bg-red-600"
            @click="logout"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import LottieLoader from "../components/loader/LottieLoader.vue";
import { useAuthStore } from "@/store/auth";
import { rememberRouteForNextLogin } from "@/utils/authRedirect";
import { useRenewalNotifications } from "@/composables/useRenewalNotifications";
import ClientProfileSwitcher from "@/components/ClientProfileSwitcher.vue";

export default {
  name: "HeaderComponent",
  components: { LottieLoader, ClientProfileSwitcher },
  props: {
    /** Mobile drawer open state (for aria-expanded on menu control). */
    sidebarOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["toggleSidebar"],
  setup() {
    return useRenewalNotifications();
  },
  data() {
    return {
      showDropdown: false,
      showNotifications: false,
      showLogoutModal: false,
      showLogoutLoader: false,
    };
  },
  computed: {
    ...mapState(useAuthStore, [
      "companyName",
      "displayEmail",
      "userType",
      "isSuperAdmin",
    ]),
    /** Check if user is Super Admin (user_level = 1) */
    userType() {
      console.log(useAuthStore().userType);
      return useAuthStore().userType;
    },
    isSuperAdmin() {
      return useAuthStore().userLevel === "1";
    },
    canViewNotifications() {
      if (this.userType === "client") return false;
      return ["1", "2"].includes(useAuthStore().userLevel);
    },
    /** First two name words' initials (e.g. "John Doe Dela Cruz" → "JD"). */
    profileInitials() {
      const name = (this.companyName || "").trim();
      if (!name) return "?";
      const words = name.split(/\s+/).filter(Boolean);
      if (words.length >= 2) {
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      }
      return words[0].charAt(0).toUpperCase();
    },
    /** First word + initial of second word (e.g. "John Doe Dela Cruz" → "John D."). */
    headerDisplayName() {
      const name = (this.companyName || "").trim();
      if (!name) return "";
      const words = name.split(/\s+/).filter(Boolean);
      if (words.length >= 2) {
        return `${words[0]} ${words[1].charAt(0).toUpperCase()}.`;
      }
      return words[0];
    },
    profileRoleLabel() {
      if (this.userType === "client") return "";
      if (this.isSuperAdmin) return "Super Admin";

      const memberLevels = new Set(["3", "4", "5", "7", "8"]);
      return memberLevels.has(useAuthStore().userLevel) ? "Member" : "Admin";
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("storage", this.handleProfileSwitch);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("storage", this.handleProfileSwitch);
  },
  methods: {
    handleProfileSwitch(event) {
      if (event.key === "dmsuite:client-profile-switched" && this.userType === "client") {
        window.location.replace(this.$router.resolve("/").href);
      }
    },
    toggleDropdown(event) {
      event.stopPropagation();
      this.showNotifications = false;
      this.showDropdown = !this.showDropdown;
    },
    toggleNotifications() {
      this.showDropdown = false;
      this.showNotifications = !this.showNotifications;
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.showDropdown = false;
      }
      if (
        this.$refs.notificationPanel &&
        !this.$refs.notificationPanel.contains(event.target)
      ) {
        this.showNotifications = false;
      }
    },
    isNotificationRead(notificationId) {
      return this.seenIds.includes(notificationId);
    },
    notificationToneClass(tone) {
      if (tone === "danger") return "bg-red-500";
      if (tone === "warning") return "bg-amber-500";
      return "bg-blue-500";
    },
    markAllAsReadAndClose() {
      this.markAllNotificationsAsRead();
      this.showNotifications = false;
    },
    openNotification(notification) {
      this.markNotificationAsRead(notification.id);
      this.showNotifications = false;
      this.$router.push({
        path: "/settings/notifications",
        query: {
          notificationFilter: notification.filterId,
          notificationId: notification.id,
        },
      });
    },
    openLogoutModal() {
      this.showLogoutModal = true;
      this.showDropdown = false;
      this.showNotifications = false;
    },
    async logout() {
      this.showLogoutModal = false;
      this.showLogoutLoader = true;
      try {
        const authStore = useAuthStore();
        if (!authStore.isTaskOnlyInternalUser) {
          rememberRouteForNextLogin(this.$route.fullPath, authStore);
        }
        await authStore.logout();
      } finally {
        setTimeout(() => {
          this.showLogoutLoader = false;
          this.$router.push("/");
        }, 800);
      }
    },
    navigateTo(path) {
      this.showDropdown = false;
      this.showNotifications = false;
      this.$router.push(path);
    },
    openProfilePage() {
      this.showDropdown = false;
      this.showNotifications = false;
      this.$router.push(
        this.userType === "client" ? "/client-profile" : "/profile"
      );
    },
  },
};
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 30;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media only screen and (max-width: 640px) {
  .right-profile .notifcaton {
    display: none;
  }
}

@media only screen and (max-width: 360px) {
  .header h1 {
    font-size: 1rem;
  }
  .fa-bars {
    font-size: 1.2rem;
  }
}
</style>
